import { type FC, useState } from "react";
import { Box, AppBar, TablePagination, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePostsQuery } from "@/partner/entities/posts";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { PostsListItem } from "./PostsListItem";
import { PostsListToolbar } from "./PostsListToolbar";

interface PostsListProps {
  onEditPost: (id: string) => void;
}

export const PostsList: FC<PostsListProps> = ({ onEditPost }) => {
  const { t } = useTranslation();

  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const { data, loading } = usePostsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        search,
      },
    },
  });

  const postsData = data?.posts;
  const posts = postsData?.nodes ?? [];
  const count = postsData?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(posts.map((p) => ({ id: p.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < posts.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (posts.length === 0) {
    return <EmptyListCard title={t("posts.list.empty")} />;
  }

  return (
    <Box>
      <AppBar position="static" color="default" elevation={0}>
        <PostsListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={posts}
          onSelectAllClick={handleSelectAll}
          selectedItems={selectedItems}
          onCloseSelected={() => setSelectedItems([])}
        />
      </AppBar>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {posts.map((post) => (
          <PostsListItem
            key={post.id}
            post={post}
            selected={selectedIds.includes(post.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditPost}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
